import { BCMSDate, BCMSDefaultModel, BCMSStateManagerCRUD, BCMSRecord, DisabledPrompt, useBCMSContext, useStateManager, useRoute } from "bcms-react";
import Classes, { Account, Payee } from "model";
import CustomSelect, { CustomAccountSelect, CustomSelectItem } from 'components/custom-ui/CustomSelect';
import DateSelect from 'components/custom-ui/DateSelect';
import UIRadioGroup, { UIRadio } from 'components/custom-ui/UIRadioGroup';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import { PrimaryButton, SecondaryButton, UIAmountInput, UITextInput } from 'components/ui';
import React from 'react';
import "./mobile.css";
import "./styles.css";

/**
 * @function SingleWirePage
 * @return {React.Component}
 * @exported true
 * @description The single wire page
 */

export default function SingleWirePage(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
              .update([ "data", prop ], value)
              .submit(setState);
      },
      
      onWireChange: prop => value => {
        BCMSStateManagerCRUD.init(state)
              .update([ "data", "wire", prop ], value)
              .submit(setState);
      }
  });

  return(
    <PageContainer className="single-wire-page" state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => {
  const defaultWire = BCMSDefaultModel.wireHistory();
  
  return ({
    futureWireDate: undefined, // wire property since undefined cant be set in the wire instance.
    wire: defaultWire,
    linkedAccounts: Account.initAllAsset(),
    payees: Payee.initAll(),
    recurringEndByDate: undefined, // wire property since undefined cant be set in the wire instance.
    recurringWireDate: undefined, // wire property since undefined cant be set in the wire instance.
    /*amount: undefined,
    futureWireDate: undefined,
    linkedAccounts: LinkedAccount.initAll(),
    memo: undefined,
    payees: Payee.initAll(),
    recurringEndByDate: undefined,
    recurringWireDate: undefined,
    recurringNumberOfTransfers: undefined,
    selectedLinkedAccountID: "fundingAccount",
    selectedPayeeID: "selectPayee",
    selectedSchedulingOptionID: "selectSchedule",
    selectedFrequencyID: "monthly",
    selectedFrequencyTypeID: "noEndDate"*/
  });
}

export const getDefaultUI = () => ({
  // no ui changes for now
});

function PageContent(props) {
  const { data, onChange, onWireChange } = useStateManager();
  const navigate = useRoute();

  const payeeItems = data.payees.reduce((acc, payee, index) => {
    if(index === 0) {
      acc.push(<CustomSelectItem key="selectPayee" value="selectPayee">-- Select Payee --</CustomSelectItem>);
    }
    acc.push(<CustomSelectItem key={ payee.id } value={ payee.id }>{ payee.nickname }</CustomSelectItem>);
    return acc;
  }, []);

  const linkedAccountItems = data.linkedAccounts.reduce((acc, account, index) => {
    const label = `${account.number} - ${account.nickname} (Available ${ account.availableBalance.format() })`;
    if(index === 0) {
      acc.push(<CustomSelectItem key={ "fundingAccount" } value="fundingAccount">-- Select a Funding Account --</CustomSelectItem>);
    } else {
      acc.push(<CustomSelectItem key={ account.id } value={ account.id }>{ label }</CustomSelectItem>);
    }
    
    return acc;
  }, []);

  const scheduleOptions = [
    {id: "selectSchedule", name:"-- Select an Option --" },
    {id: "future", name:"Future (One Time)" },
    {id: "recurring", name:"Recurring" }
  ];
  const scheduleOptionItems = scheduleOptions.reduce((acc, option) => {
    acc.push(<CustomSelectItem key={ option.id } value={ option.id }>{ option.name }</CustomSelectItem>);
    return acc;
  }, []);

  const cancel = () => navigate("/wiresActivity");



  return (
  <PageContentView>
    <CenterContentContainer>
      <ContentHeaderView text="Single Wire" />
      <FormContainer className="small-form-width">
        <FormRowView text="* Payee">
          <CustomSelect value={ data.wire.payee ? data.wire.payee.id : "selectPayee" } onChange={ onWireChange("payee") }>
            { payeeItems }
          </CustomSelect>
        </FormRowView>
        <FormRowView text="* Funding Account">
          <CustomSelect value={ data.wire.fundingAccount ? data.wire.fundingAccount.id : "fundingAccount" } onChange={ onWireChange("fundingAccount") }>
            { linkedAccountItems }
          </CustomSelect>
        </FormRowView>
        <FormRowView text="* Amount">
          <UIAmountInput value={ data.wire.amount ? data.wire.amount.amount : undefined } onChange={ onChange("amount") } />
        </FormRowView>
        <FormRowView text="* Scheduling Option">
          <CustomSelect value={ data.wire.schedulingOption ? data.wire.schedulingOption : "selectSchedule" } onChange={ onWireChange("schedulingOption") }>
            { scheduleOptionItems }
          </CustomSelect>
        </FormRowView>
        { data.wire.schedulingOption === "future" && 
        <div className="recurring-options-container">
          <FormRowView text="* Wire Date">
            <DateSelect value={ data.futureWireDate } onChange={ onChange("futureWireDate") }/>
          </FormRowView>
          </div>
        }
        { data.wire.schedulingOption === "recurring" && 
        <div className="recurring-options-container">
          <FormRowView text="* Wire Date">
            <DateSelect value={ data.recurringWireDate } onChange={ onChange("recurringWireDate") }/>
          </FormRowView>
          <FormRowView text="* Frequency">
            <CustomSelect value={ data.wire.frequency }>
              <CustomSelectItem value="daily">Daily</CustomSelectItem>
              <CustomSelectItem value="weekly">Weekly</CustomSelectItem>
              <CustomSelectItem value="everyTwoWeeks">Every two weeks</CustomSelectItem>
              <CustomSelectItem value="twiceAMonth">Twice a month</CustomSelectItem>
              <CustomSelectItem value="everyFourWeeks">Every four weeks</CustomSelectItem>
              <CustomSelectItem value="monthly">Monthly</CustomSelectItem>
              <CustomSelectItem value="everyTwoMonths">Every two months</CustomSelectItem>
              <CustomSelectItem value="quarterly">Quarterly</CustomSelectItem>
              <CustomSelectItem value="everySixMonths">Every six months</CustomSelectItem>
              <CustomSelectItem value="annually">Annually</CustomSelectItem>
            </CustomSelect>
          </FormRowView>
          <UIRadioGroup value={ data.wire.frequencyType } onChange={ onWireChange("frequencyType") }>
            <FormRowView className="no-end-date-radio">
              <UIRadio name="frequency" value="noEndDate" label="No End Date" />
            </FormRowView>
            <FormRowView>
              <UIRadio name="frequency" value="endBy" label="End By:" />
              <DateSelect value={ data.recurringEndByDate } onChange={ onChange("recurringEndByDate") }/>
            </FormRowView>
            <FormRowView>
              <UIRadio name="frequency" value="numberOfTransfers" label="Number of Transfers:" />
              <UITextInput value={ data.wire.recurringNumberOfTransfers } onChange={ onWireChange("recurringNumberOfTransfers") } />
            </FormRowView>
          </UIRadioGroup>
        </div>
        }
        <FormRowView text="Memo">
          <UITextInput value={ data.wire.memo } onChange={ onWireChange("memo") } />
        </FormRowView>
        <FormButtonRow>
          <SecondaryButton text="Cancel" onClick={ cancel } />
          <DisabledPrompt>
            <PrimaryButton text="Submit" />
          </DisabledPrompt>
        </FormButtonRow>
        
      </FormContainer>
    </CenterContentContainer>
  </PageContentView>
  );
}